import type { AxiosResponse } from "axios";

/**
 * Network diagnostics for SSE stuck/fallback events.
 *
 * Captures signals that help distinguish corporate-proxy interference from
 * other network issues (bad wifi, mobile, firewall, genuine offline). Run
 * on demand when an SSE stuck event is about to be reported to Sentry.
 *
 * Sources:
 *  - `navigator.*` — online status, connection type, RTT, service worker
 *  - `performance.getEntriesByType("resource")` — HTTP version, TLS timing,
 *    TTFB for the most recent SSE endpoint request
 *  - last-seen response headers from the configure POST, captured via
 *    `captureConfigureHeaders` on each successful configure
 */

export interface StreamNetworkDiagnostics {
  online: boolean;
  connectionType?: string;
  rtt?: number;
  downlink?: number;
  saveData?: boolean;
  hasServiceWorker: boolean;
  responseHeaders?: Record<string, string>;
  vendorProxyHeaders?: Record<string, string>;
  resourceTiming?: {
    nextHopProtocol?: string;
    tlsHandshakeMs?: number;
    ttfbMs?: number;
    durationMs?: number;
    transferSize?: number;
  };
}

/** Generic proxy-detection headers (RFC + common intermediary headers). */
const GENERIC_PROXY_HEADERS = [
  "via",
  "server",
  "age",
  "cache-control",
  "connection",
  "x-cache",
  "x-cache-lookup",
  "x-proxy-agent",
  "x-forwarded-for",
  "x-forwarded-proto",
  "x-real-ip",
];

/** Known enterprise proxy / security appliance vendor header prefixes. */
const VENDOR_PROXY_PREFIXES = [
  "x-bluecoat",
  "x-bc-",
  "x-zscaler",
  "x-zs-",
  "x-forcepoint",
  "x-netskope",
  "x-iboss",
  "x-cisco",
  "x-ironport",
  "x-symantec",
  "x-palo-alto",
  "x-panw",
  "x-fortinet",
  "x-fortigate",
  "x-checkpoint",
  "x-mcafee",
  "x-trend",
  "x-sophos",
  "x-barracuda",
  "x-websense",
  "x-squid",
];

interface CapturedHeaders {
  responseHeaders: Record<string, string>;
  vendorProxyHeaders: Record<string, string>;
  capturedAt: number;
}

// Per-connection store of last-seen response headers from the configure POST
const lastKnownHeaders = new Map<string, CapturedHeaders>();

/**
 * Extract interesting headers from a configure POST response and stash them
 * for later inclusion in stuck/fallback Sentry events. Best-effort — any
 * failure here is swallowed so diagnostics never break the real flow.
 */
export function captureConfigureHeaders(
  connectionName: string,
  response: AxiosResponse
): void {
  try {
    const rawHeaders = response.headers ?? {};
    const normalized: Record<string, string> = {};
    for (const [k, v] of Object.entries(rawHeaders)) {
      if (typeof v === "string") normalized[k.toLowerCase()] = v;
      else if (Array.isArray(v)) normalized[k.toLowerCase()] = v.join(", ");
    }

    const responseHeaders: Record<string, string> = {};
    for (const name of GENERIC_PROXY_HEADERS) {
      if (normalized[name]) responseHeaders[name] = normalized[name];
    }

    const vendorProxyHeaders: Record<string, string> = {};
    for (const [name, value] of Object.entries(normalized)) {
      if (VENDOR_PROXY_PREFIXES.some((p) => name.startsWith(p))) {
        vendorProxyHeaders[name] = value;
      }
    }

    lastKnownHeaders.set(connectionName, {
      responseHeaders,
      vendorProxyHeaders,
      capturedAt: Date.now(),
    });
  } catch {
    // Swallow — diagnostics must never break the real flow
  }
}

/** Snapshot current navigator / resource-timing / header state for a connection. */
export function getStreamNetworkDiagnostics(
  connectionName: string,
  streamUrl: string
): StreamNetworkDiagnostics {
  const diagnostics: StreamNetworkDiagnostics = {
    online: typeof navigator !== "undefined" ? navigator.onLine : true,
    hasServiceWorker:
      typeof navigator !== "undefined" &&
      !!navigator.serviceWorker?.controller,
  };

  try {
    // NetworkInformation isn't in the TS lib; guard all access.
    const conn = (
      navigator as Navigator & { connection?: Record<string, unknown> }
    ).connection;
    if (conn) {
      if (typeof conn.effectiveType === "string")
        diagnostics.connectionType = conn.effectiveType;
      if (typeof conn.rtt === "number") diagnostics.rtt = conn.rtt;
      if (typeof conn.downlink === "number") diagnostics.downlink = conn.downlink;
      if (typeof conn.saveData === "boolean")
        diagnostics.saveData = conn.saveData;
    }
  } catch {
    // Best-effort
  }

  const captured = lastKnownHeaders.get(connectionName);
  if (captured) {
    if (Object.keys(captured.responseHeaders).length > 0) {
      diagnostics.responseHeaders = captured.responseHeaders;
    }
    if (Object.keys(captured.vendorProxyHeaders).length > 0) {
      diagnostics.vendorProxyHeaders = captured.vendorProxyHeaders;
    }
  }

  try {
    const entries = performance.getEntriesByType(
      "resource"
    ) as PerformanceResourceTiming[];
    // Match entries for the stream endpoint OR the configure endpoint.
    // Most-recent-first so we describe the current attempt, not an ancient one.
    const streamEntries = entries
      .filter(
        (e) =>
          e.name.includes(streamUrl) ||
          e.name.includes("/api/stream") ||
          e.name.includes("/api/stream/configure")
      )
      .sort((a, b) => b.startTime - a.startTime);

    const latest = streamEntries[0];
    if (latest) {
      const timing: StreamNetworkDiagnostics["resourceTiming"] = {};
      if (latest.nextHopProtocol) timing.nextHopProtocol = latest.nextHopProtocol;
      if (latest.secureConnectionStart > 0 && latest.connectEnd > 0) {
        timing.tlsHandshakeMs = Math.round(
          latest.connectEnd - latest.secureConnectionStart
        );
      }
      if (latest.responseStart > 0 && latest.startTime >= 0) {
        timing.ttfbMs = Math.round(latest.responseStart - latest.startTime);
      }
      if (latest.duration > 0) {
        timing.durationMs = Math.round(latest.duration);
      }
      if (typeof latest.transferSize === "number") {
        timing.transferSize = latest.transferSize;
      }
      if (Object.keys(timing).length > 0) {
        diagnostics.resourceTiming = timing;
      }
    }
  } catch {
    // Best-effort
  }

  return diagnostics;
}
